import React, { useState, useEffect } from "react";
import "./../../pages/css/MaintenanceRequest.css";
import { RiImageAddLine } from "react-icons/ri";
import Select from "react-select";
import { toast } from "sonner";
import { usePostRepairRequestMutation } from "./../../slices/maintenanceApiSlice";
import { useGetAcademyQuery } from "./../../slices/userApiSlice";
import { useGetAssetQuery } from "./../../slices/assetApiSlice";
import CreatableSelect from "react-select/creatable";
const priorities = [
  { value: "Immediate", label: "Immediate (Within 24 hours)" },
  { value: "High", label: "High (Within 1-2 days)" },
  { value: "Moderate", label: "Moderate (Within 1 week)" },
  { value: "Low", label: "Low (More than a week)" },
];

const MaintenanceRequest = () => {
  const [formData, setFormData] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    priority: "",
    academy: "",
    area: "",
    description: "",
    assetName: "",
    scheduled: false,
    assetCode: "",
    images: [],
    status: "Pending",
    // academyId:"",
  });
  // console.log("formdata", formData);

  const [postRepairRequest, { isLoading: requesting }] =
    usePostRepairRequestMutation();
  const { data: academies = [], isLoading, error } = useGetAcademyQuery();
  const { data: assets = [], error1 } = useGetAssetQuery();

  const [areaList, setAreaList] = useState([]);
  const [images, setImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [imageError, setImageError] = useState("");

  useEffect(() => {
    if (formData.academy && assets.length > 0) {
      const matchedAssets = assets.filter(
        (asset) => asset.academyID === formData.academy,
      );

      const filteredAreas = matchedAssets
        .map((asset) => asset.assetArea)
        .filter((area, index, self) => area && self.indexOf(area) === index);

      setAreaList((prev) => {
        const same =
          prev.length === filteredAreas.length &&
          prev.every((val, i) => val === filteredAreas[i]);
        return same ? prev : filteredAreas;
      });
    } else {
      setAreaList((prev) => (prev.length === 0 ? prev : []));
    }
  }, [formData.academy, assets]);

  // Field validation functions
  const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex = /^(17|77)\d{6}$/;
    if (!phoneNumber.trim()) return "Phone number is required.";
    if (!phoneRegex.test(phoneNumber))
      return "Phone must start with 17 or 77 and be 8 digits.";
    return "";
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) return "Email is required.";
    if (!emailRegex.test(email)) return "Enter a valid email address.";
    return "";
  };

  const validateName = (name) => {
    if (!name.trim()) return "Name is required.";
    return "";
  };

  const validateAssetName = (assetName) => {
    if (!assetName.trim()) return "Asset name is required.";
    return "";
  };

  const validateDescription = (description) => {
    if (!description.trim()) return "Description is required.";
    return "";
  };

  const handleInputChange = (e, field) => {
    const { name, value } = e.target || {};

    if (field) {
      setFormData({ ...formData, [field]: e.value });

      // Validate field after selection
      if (field === "priority") {
        setErrors({
          ...errors,
          priority: e.value ? "" : "Please select a priority.",
        });
      } else if (field === "area") {
        setErrors({ ...errors, area: e.value ? "" : "Please select an area." });
      }
    } else {
      setFormData({ ...formData, [name]: value });

      // Validate in real-time as user types
      if (name === "phoneNumber") {
        setErrors({ ...errors, phoneNumber: validatePhoneNumber(value) });
      } else if (name === "email") {
        setErrors({ ...errors, email: validateEmail(value) });
      } else if (name === "name") {
        setErrors({ ...errors, name: validateName(value) });
      } else if (name === "assetName") {
        setErrors({ ...errors, assetName: validateAssetName(value) });
      } else if (name === "description") {
        setErrors({ ...errors, description: validateDescription(value) });
      }
    }
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    // No file size restrictions - accept all files
    setImageError("");
    setImages((prevImages) => [...prevImages, ...files]);
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    let newErrors = {};

    newErrors.name = validateName(formData.name);
    newErrors.phoneNumber = validatePhoneNumber(formData.phoneNumber);
    newErrors.email = validateEmail(formData.email);
    newErrors.assetName = validateAssetName(formData.assetName);
    newErrors.academy = formData.academy ? "" : "Please select an academy.";
    newErrors.area = formData.area ? "" : "Please select an area.";
    newErrors.priority = formData.priority ? "" : "Please select a priority.";
    newErrors.description = validateDescription(formData.description);

    // Filter out empty error messages
    const filteredErrors = Object.fromEntries(
      Object.entries(newErrors).filter(([_, value]) => value !== ""),
    );

    setErrors(filteredErrors);
    return Object.keys(filteredErrors).length === 0;
  };

  // Handle academy selection
  const handleAcademyChange = (selectedOption) => {
    setFormData({
      ...formData,
      academy: selectedOption ? selectedOption.value : "",
      academyId: selectedOption ? selectedOption.value : "",
      area: "",
    });

    setErrors({
      ...errors,
      academy: selectedOption ? "" : "Please select an academy.",
    });
  };

  const isFormComplete = () => {
    // Check if any required field is empty
    return (
      formData.name.trim() !== "" &&
      validatePhoneNumber(formData.phoneNumber) === "" &&
      validateEmail(formData.email) === "" &&
      formData.assetName.trim() !== "" &&
      formData.academy !== "" &&
      formData.area !== "" &&
      formData.priority !== "" &&
      formData.description.trim() !== ""
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Check if form is empty without setting error messages
    if (!isFormComplete()) {
      // Show simple SweetAlert for empty form
      toast.warning("Form Incomplete", {
        description:
          "Please fill in all required fields to submit the request.",
      });
      return;
    }

    // Now validate with error messages for any remaining issues
    if (!validateForm()) return;

    const {
      name,
      phoneNumber,
      email,
      priority,
      status,
      area,
      description,
      assetName,
      academy,
    } = formData;

    const requestData = new FormData();
    requestData.append("name", name.trim());
    requestData.append("phoneNumber", phoneNumber.trim());
    requestData.append("email", email.trim());
    requestData.append("priority", priority.trim());
    requestData.append("status", status || "Pending");
    requestData.append("area", area);
    requestData.append("description", description.trim());
    requestData.append("assetName", assetName.trim());
    requestData.append("scheduled", "false");
    requestData.append("assetCode", ""); // null as empty string

    images.forEach((imageFile) => {
      requestData.append("images", imageFile);
    });
    requestData.append("academyId", academy.trim());

    toast("Are you sure?", {
      description: "Do you want to send this repair request?",
      action: {
        label: "Yes, send it!",
        onClick: async () => {
          try {
            await postRepairRequest(requestData).unwrap();

            toast.success("Repair Request Sent!");

            setFormData({
              name: "",
              phoneNumber: "",
              email: "",
              priority: "",
              status: "Pending",
              academy: "",
              area: "",
              description: "",
              assetName: "",
              scheduled: false,
              assetCode: "",
              images: [],
              academyId: "",
            });
            setImages([]);
            setErrors({});
          } catch (err) {
            console.error(
              "Error sending request. Make sure you filled out all the fields.",
              err,
            );
            toast.error("Failed to send Repair Request", {
              description:
                err?.data?.message ||
                "Make sure you filled out all the fields.",
            });
          }
        },
      },
    });
  };

  return (
    <div className="mr-container">
      <div className="mr-header">
        <h1 className="mr-main-title">Maintenance Request</h1>
        <div className="fb-title-accent">
          <span></span>
          <span></span>
          <span></span>
        </div>

        <p className="mr-description">
          Submit official requests for facility maintenance or repairs. Accurate
          details ensure timely assessment and resolution by our technical team.
        </p>
      </div>

      <div className="mr-card">
        <form className="mr-form" onSubmit={handleSubmit}>
          {/* Section 1: Reporter Information */}
          <div className="mr-section">
            <div className="mr-section-header">
              <span className="mr-step">01</span>
              <div>
                <h3 className="mr-section-name">Requester Details</h3>
                <p className="mr-section-desc">
                  Provide the contact information of the person reporting the
                  issue.
                </p>
              </div>
            </div>

            <div className="mr-fields-grid">
              <div className="mr-field-box">
                <label className="mr-label">Full Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`mr-input ${errors.name ? "error" : ""}`}
                  placeholder="Enter your full name"
                />
                {errors.name && (
                  <span className="mr-error-msg">{errors.name}</span>
                )}
              </div>

              <div className="mr-field-box">
                <label className="mr-label">Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className={`mr-input ${errors.phoneNumber ? "error" : ""}`}
                  placeholder="e.g., 17123456"
                />
                {errors.phoneNumber && (
                  <span className="mr-error-msg">{errors.phoneNumber}</span>
                )}
              </div>

              <div className="mr-field-box">
                <label className="mr-label">Email Address</label>
                <input
                  type="text"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`mr-input ${errors.email ? "error" : ""}`}
                  placeholder="official.email@example.bt"
                />
                {errors.email && (
                  <span className="mr-error-msg">{errors.email}</span>
                )}
              </div>
            </div>
          </div>

          {/* Section 2: Issue Details */}
          <div className="mr-section">
            <div className="mr-section-header">
              <span className="mr-step">02</span>
              <div>
                <h3 className="mr-section-name">Incident Specification</h3>
                <p className="mr-section-desc">
                  Identify the precise location and nature of the required
                  maintenance.
                </p>
              </div>
            </div>

            <div className="mr-fields-grid">
              <div className="mr-field-box">
                <label className="mr-label">Academy</label>
                <Select
                  classNamePrefix="fms-select"
                  name="academy"
                  onChange={handleAcademyChange}
                  options={academies.map((a) => ({
                    value: a.academyId,
                    label: a.name,
                  }))}
                  isLoading={isLoading}
                  isClearable
                  placeholder="Select Academy..."
                />
                {errors.academy && (
                  <span className="mr-error-msg">{errors.academy}</span>
                )}
              </div>

              <div className="mr-field-box">
                <label className="mr-label">Area / Location</label>
                <CreatableSelect
                  classNamePrefix="fms-select"
                  name="area"
                  value={
                    formData.area
                      ? { value: formData.area, label: formData.area }
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleInputChange(selectedOption, "area")
                  }
                  onCreateOption={(inputValue) => {
                    const newOption = { value: inputValue, label: inputValue };
                    setAreaList((prev) => [...prev, inputValue]);
                    handleInputChange(newOption, "area");
                  }}
                  options={areaList.map((area) => ({
                    value: area,
                    label: area,
                  }))}
                  placeholder="Specify block, floor, or room number"
                  isClearable
                />
                {errors.area && (
                  <span className="mr-error-msg">{errors.area}</span>
                )}
              </div>

              <div className="mr-field-box">
                <label className="mr-label">Priority Level</label>
                <Select
                  classNamePrefix="fms-select"
                  name="priority"
                  value={
                    formData.priority
                      ? priorities.find((p) => p.value === formData.priority)
                      : null
                  }
                  onChange={(selectedOption) =>
                    handleInputChange(selectedOption, "priority")
                  }
                  options={priorities}
                  placeholder="Select Priority..."
                  isClearable
                />
                {errors.priority && (
                  <span className="mr-error-msg">{errors.priority}</span>
                )}
              </div>

              <div className="mr-field-box full">
                <label className="mr-label">Asset Name / Title</label>
                <input
                  type="text"
                  name="assetName"
                  value={formData.assetName}
                  onChange={handleInputChange}
                  className={`mr-input ${errors.assetName ? "error" : ""}`}
                  placeholder="Specify the asset or system (e.g., Electrical Panel, HVAC)"
                />
                {errors.assetName && (
                  <span className="mr-error-msg">{errors.assetName}</span>
                )}
              </div>

              <div className="mr-field-box full">
                <label className="mr-label">Description of Issue</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className={`mr-input mr-textarea ${
                    errors.description ? "error" : ""
                  }`}
                  placeholder="Provide a detailed account of the issue, including specific observations..."
                />
                {errors.description && (
                  <span className="mr-error-msg">{errors.description}</span>
                )}
              </div>
            </div>
          </div>

          {/* Section 3: Attachments */}
          <div className="mr-section no-border">
            <div className="mr-section-header">
              <span className="mr-step">03</span>
              <div>
                <h3 className="mr-section-name">Supporting Documentation</h3>
                <p className="mr-section-desc">
                  Attach visual evidence to assist our technical team in their
                  assessment (Maximum 5 images).
                </p>
              </div>
            </div>

            <div className="mr-upload-zone">
              <input
                type="file"
                accept="image/*"
                id="imageUpload"
                multiple
                onChange={handleImageUpload}
                style={{ display: "none" }}
              />

              <div className="mr-upload-list">
                {images.map((imgFile, index) => (
                  <div key={index} className="mr-upload-item">
                    <img
                      src={URL.createObjectURL(imgFile)}
                      alt={`Issue ${index + 1}`}
                    />
                    <button
                      type="button"
                      className="mr-item-remove"
                      onClick={() => removeImage(index)}
                    >
                      &times;
                    </button>
                  </div>
                ))}

                {images.length < 5 && (
                  <button
                    type="button"
                    className="mr-add-photo-btn"
                    onClick={() =>
                      document.getElementById("imageUpload").click()
                    }
                  >
                    <RiImageAddLine className="mr-icon" />
                    <span>Upload Photo</span>
                  </button>
                )}
              </div>
              {imageError && (
                <p className="mr-error-text centered">{imageError}</p>
              )}
            </div>
          </div>

          <div className="mr-form-actions">
            <button
              disabled={requesting}
              type="submit"
              className="mr-submit-btn"
            >
              {requesting ? (
                <>
                  <span className="mr-spinner-dot"></span> Processing...
                </>
              ) : (
                "Submit"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaintenanceRequest;
